import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useEffect, useRef, useState } from 'react'
import type { FeatureFlag } from '../feature-flag-types'

/**
 * Alarga tipos literais boolean (true/false) para boolean.
 * Permite que flags com defaultValue: false sejam tipadas como boolean em vez de literal false.
 */
type WidenBoolean<T> = T extends boolean ? boolean : T

/**
 * Infere o tipo de uma feature flag a partir do seu defaultValue.
 * Flags boolean são alargadas para boolean; outros tipos são preservados.
 */
type InferFlagType<T> = T extends { readonly defaultValue: infer D } ? WidenBoolean<D> : unknown

type FlagValues<T extends Record<string, FeatureFlag>> = { [K in keyof T]: InferFlagType<T[K]> }

/**
 * Hook para acessar feature flags com reatividade seletiva.
 *
 * Usa `useLDClient()` + listeners `change:flagKey` por flag, ao invés de `useFlags()`.
 * O componente só re-renderiza quando uma das flags que ele assina muda —
 * não em toda e qualquer mudança de flag no app.
 *
 * Seguro após identificação: o cliente LD é um singleton. Quando `identifyUser()` é chamado
 * após o login, o SDK dispara eventos `change:flagKey` para as flags que mudaram de valor,
 * que são capturados pelos listeners registrados aqui.
 *
 * @param flags - Objeto mapeando chaves locais para definições de flags
 * @returns Objeto com os valores das flags — tipos inferidos automaticamente do defaultValue
 *
 * @example
 * // Flag única
 * const { i18nEnabled } = useFeatureFlags({
 *   i18nEnabled: FeatureFlagName.I18N
 * })
 *
 * @example
 * // Múltiplas flags
 * const { i18n, userSpace } = useFeatureFlags({
 *   i18n: FeatureFlagName.I18N,
 *   userSpace: FeatureFlagName.USER_SPACE_ENABLED
 * })
 *
 * @example
 * // Flag de array com override de tipo
 * const { helpCenter } = useFeatureFlags({
 *   helpCenter: FeatureFlagName.COMPANIES_WITH_CUSTOM_HELP_CENTER
 * })
 */
export function useFeatureFlags<T extends Record<string, FeatureFlag>>(flags: T): FlagValues<T> {
  const ldClient = useLDClient()

  // O ref evita que o objeto inline de flags (recriado a cada render) dispare
  // o useEffect desnecessariamente.
  const flagsRef = useRef(flags)

  // Inicializa com valores reais se o cliente já estiver pronto, caso contrário usa defaults.
  // Cobre o caso comum onde o LD inicializa antes do componente montar.
  const [values, setValues] = useState<FlagValues<T>>(() => {
    const results: Record<string, unknown> = {}
    for (const key in flags) {
      const flag = flags[key]
      results[key] = ldClient?.variation(flag.name, flag.defaultValue) ?? flag.defaultValue
    }
    return results as FlagValues<T>
  })

  useEffect(() => {
    if (!ldClient) return

    // Definido dentro do effect para sempre fechar sobre o ldClient atual.
    const read = (): FlagValues<T> => {
      const results: Record<string, unknown> = {}
      for (const key in flagsRef.current) {
        const flag = flagsRef.current[key]
        results[key] = ldClient.variation(flag.name, flag.defaultValue)
      }
      return results as FlagValues<T>
    }

    // Sincroniza imediatamente: cobre o gap entre o render inicial e a execução do effect,
    // e também re-sincroniza após identifyUser() resolver (contexto do usuário atualizado).
    // Esta leitura inicial NÃO é jitterada — é o mount do próprio componente, não um broadcast.
    setValues(read())

    // Anti thundering herd: ao receber uma mudança de flag via stream, espalha a aplicação
    // por uma janela aleatória (0..FLAG_CHANGE_JITTER_MS) para evitar que todos os clientes
    // de uma empresa re-renderizem/refetchem no mesmo instante.

    const FLAG_CHANGE_JITTER_MS = 30_000
    // Um único timer ativo para o hook inteiro. Como `read()` lê todas as flags de uma vez,
    // mudanças simultâneas (ex.: várias flags após identifyUser()) reusam o timer já agendado
    // ao invés de cada uma agendar o seu — evita re-renders redundantes.
    let activeTimer: ReturnType<typeof setTimeout> | null = null

    // Assina apenas as flags que este componente usa.
    // Cada evento `change:flagKey` dispara somente quando aquela flag específica muda —
    // completamente isolado de mudanças em outras flags do app.
    const subscriptions = Object.values(flagsRef.current).map(flag => {
      const handler = () => {
        if (FLAG_CHANGE_JITTER_MS <= 0) {
          setValues(read())
          return
        }
        // Já existe um timer agendado: ignora esta mudança. Quando o timer disparar,
        // `read()` lerá os valores atualizados de TODAS as flags de uma vez — não há
        // necessidade de agendar um timer por flag. Isso coalesce um burst de mudanças
        // simultâneas em um único re-render.
        if (activeTimer) return
        // Nenhum timer ativo: agenda o re-render com jitter aleatório. Ao disparar, limpa
        // a referência (liberando o próximo burst a agendar) e aplica os valores atuais.
        activeTimer = setTimeout(() => {
          activeTimer = null
          setValues(read())
        }, Math.random() * FLAG_CHANGE_JITTER_MS)
      }
      ldClient.on(`change:${flag.name}`, handler)
      return { key: flag.name, handler }
    })

    // Remove todos os listeners e cancela o timer pendente quando o componente
    // desmonta ou o ldClient muda — evita setState após unmount.
    return () => {
      subscriptions.forEach(({ key, handler }) => ldClient.off(`change:${key}`, handler))

      if (activeTimer) {
        clearTimeout(activeTimer)
      }
    }
  }, [ldClient])

  return values
}
